// Contact-detail presentation: names, phone numbers, locations.

// Two-letter initials from a person's name OR an email address. For emails
// we strip the domain and split the local-part on . _ or - so
// "lukas.johansson@solvigo.ai" → "LJ" instead of just "L".
export const initials = (raw: string): string => {
  if (!raw) return '?'
  const trimmed = raw.trim()
  const local = trimmed.includes('@') ? trimmed.split('@')[0] : trimmed
  const parts = local.split(/[\s._-]+/).filter(Boolean)
  if (parts.length === 0) return '?'
  return parts.map(p => p[0]).slice(0, 2).join('').toUpperCase()
}

// Normalize a raw phone number into a uniform international display format.
// Input data is messy — leading 0, +46 (0), embedded dashes/em-dashes/parens,
// 00-prefix, no-separator runs, etc. We strip all of that, detect the country
// code (or infer from `country` ISO-2), then re-emit with consistent grouping.
//
// Goal: consistent, scannable strings — not 100% officially-correct per area
// code. "+46 70 552 86 36" looks the same whether the source was "070-552 86 36",
// "+46 70 – 552 86 36" or "+46(0)70552 86 36".
const CC_LIST = ['+358', '+46', '+45', '+47', '+44', '+49', '+1'] as const
const CC_BY_COUNTRY: Record<string, string> = {
  SE: '+46', DK: '+45', NO: '+47', FI: '+358', GB: '+44', DE: '+49', US: '+1',
}

function groupSwedish(local: string): string {
  if (local.length === 9) {
    return `${local.slice(0, 2)} ${local.slice(2, 5)} ${local.slice(5, 7)} ${local.slice(7, 9)}`
  }
  if (local.length === 8) {
    return `${local.slice(0, 1)} ${local.slice(1, 4)} ${local.slice(4, 6)} ${local.slice(6, 8)}`
  }
  if (local.length === 7) {
    return `${local.slice(0, 1)} ${local.slice(1, 3)} ${local.slice(3, 5)} ${local.slice(5, 7)}`
  }
  return local.match(/.{1,3}/g)?.join(' ') || local
}

function groupPairs(local: string): string {
  if (local.length === 8) {
    return `${local.slice(0, 2)} ${local.slice(2, 4)} ${local.slice(4, 6)} ${local.slice(6, 8)}`
  }
  return local.replace(/(\d{2})(?=\d)/g, '$1 ')
}

export function formatPhone(raw?: string | null, country?: string): string {
  if (!raw) return ''
  const trimmed = raw.trim()
  if (!trimmed) return ''

  // Strip everything but digits and a leading +
  let cleaned = trimmed.replace(/[^\d+]/g, '')
  // Convert international 00-prefix to + (00 46 70 ... → +4670...)
  if (cleaned.startsWith('00')) cleaned = '+' + cleaned.slice(2)
  if (!cleaned) return trimmed

  let cc = ''
  let local = ''

  if (cleaned.startsWith('+')) {
    for (const code of CC_LIST) {
      if (cleaned.startsWith(code)) {
        cc = code
        local = cleaned.slice(code.length)
        break
      }
    }
    if (!cc) return trimmed  // unknown country code — leave as-is
  } else {
    cc = CC_BY_COUNTRY[(country || '').toUpperCase()] || '+46'
    local = cleaned
  }

  // Drop national trunk prefix (leading 0) — common in +46 (0)... or
  // bare-national-format inputs.
  if (local.startsWith('0')) local = local.slice(1)
  if (!local) return cc

  let formatted: string
  if (cc === '+46') {
    formatted = groupSwedish(local)
  } else if (cc === '+45' || cc === '+47') {
    formatted = groupPairs(local)
  } else if (cc === '+358') {
    formatted = local.length >= 8
      ? `${local.slice(0, 2)} ${local.slice(2, 5)} ${local.slice(5)}`
      : local
  } else if (cc === '+1') {
    formatted = local.length === 10
      ? `${local.slice(0, 3)} ${local.slice(3, 6)} ${local.slice(6, 10)}`
      : local
  } else {
    formatted = local.replace(/(\d{3})(?=\d)/g, '$1 ')
  }

  return `${cc} ${formatted}`.trim()
}

// Pull the ISO-2 country code out of a "City, XX" location string.
export function countryOf(location?: string | null): string | undefined {
  if (!location) return undefined
  const m = location.match(/,\s*([A-Z]{2})\s*$/i)
  return m ? m[1].toUpperCase() : undefined
}
